import { useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useClub } from '../../contexts/ClubContext';
import { db } from '../../services/firebase';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { sendSubscriptionConfirmationEmail, isEmailConfigured } from '../../services/emailService';

function ManageSubscription() {
  const { 
    club, 
    clubId,
    subscription,
    getSubscriptionStatus,
    getDaysUntilDue,
    getDaysOverdue,
    GRACE_PERIOD_DAYS
  } = useClub();
  
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const subscriptionStatus = getSubscriptionStatus();
  const daysUntilDue = getDaysUntilDue();
  const daysOverdue = getDaysOverdue();

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-TT', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getStatusBadge = () => {
    switch (subscriptionStatus) {
      case 'active':
        return <span className="status-badge active">Active</span>;
      case 'past_due':
        return <span className="status-badge past-due">Past Due</span>;
      case 'suspended':
        return <span className="status-badge suspended">Suspended</span>;
      case 'cancelled':
        return <span className="status-badge cancelled">Cancelled</span>;
      default:
        return <span className="status-badge unknown">Unknown</span>;
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    setError('');
    setMessage('');

    try {
      // In production, redirect to WiPay
      // For demo, simulate successful payment
      
      const now = new Date();
      const newPeriodEnd = new Date(now);
      newPeriodEnd.setMonth(newPeriodEnd.getMonth() + 1);

      await updateDoc(doc(db, 'clubs', clubId), {
        'subscription.status': 'active',
        'subscription.lastPaymentDate': Timestamp.fromDate(now),
        'subscription.lastPaymentAmount': 500,
        'subscription.currentPeriodEnd': Timestamp.fromDate(newPeriodEnd),
        'subscription.nextPaymentDue': Timestamp.fromDate(newPeriodEnd)
      });

      // Send confirmation email
      if (isEmailConfigured() && club?.email) {
        try {
          await sendSubscriptionConfirmationEmail(
            club.email,
            club.name,
            500,
            newPeriodEnd.toLocaleDateString()
          );
        } catch (emailErr) {
          console.error('Failed to send email:', emailErr);
        }
      }

      setMessage('Payment successful! Your subscription has been renewed.');
      
      // Reload after a moment
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (err) {
      console.error('Payment error:', err);
      setError('Payment failed. Please try again.');
    }

    setProcessing(false);
  };

  return (
    <AdminLayout title="Subscription" subtitle="Manage your club's subscription">
      <div className="subscription-content">
        {/* Status Card */}
        <div className="subscription-card status-card">
          <h2>Subscription Status</h2>
          <div className="status-display">
            {getStatusBadge()}
            <span className="club-name">{club?.name}</span>
          </div>

          {subscriptionStatus === 'active' && daysUntilDue !== null && (
            <p className="status-detail">
              {daysUntilDue > 0 
                ? `Next payment due in ${daysUntilDue} days`
                : 'Payment due today'
              }
            </p>
          )}

          {subscriptionStatus === 'past_due' && (
            <p className="status-detail warning">
              Payment is {daysOverdue} day{daysOverdue !== 1 ? 's' : ''} overdue. 
              Service will be suspended in {GRACE_PERIOD_DAYS - daysOverdue} days.
            </p>
          )}

          {subscriptionStatus === 'suspended' && (
            <p className="status-detail error">
              Your subscription has been suspended due to non-payment. 
              Please renew to restore access.
            </p>
          )}
        </div>

        {/* Details Card */}
        <div className="subscription-card details-card">
          <h2>Subscription Details</h2>
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">Plan</span>
              <span className="detail-value">ClubLink TT Monthly</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Price</span>
              <span className="detail-value">TT$ 500/month</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Start Date</span>
              <span className="detail-value">{formatDate(subscription?.startDate)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Current Period Ends</span>
              <span className="detail-value">{formatDate(subscription?.currentPeriodEnd)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Last Payment</span>
              <span className="detail-value">
                {subscription?.lastPaymentAmount 
                  ? `TT$ ${subscription.lastPaymentAmount} on ${formatDate(subscription?.lastPaymentDate)}`
                  : 'N/A'
                }
              </span>
            </div>
          </div>
        </div>

        {/* Payment Card */}
        {(subscriptionStatus === 'past_due' || subscriptionStatus === 'suspended' || 
          (subscriptionStatus === 'active' && daysUntilDue !== null && daysUntilDue <= 7)) && (
          <div className="subscription-card payment-card">
            <h2>
              {subscriptionStatus === 'active' ? 'Renew Early' : 'Make Payment'}
            </h2>
            
            <div className="payment-summary">
              <div className="payment-line">
                <span>Monthly Subscription</span>
                <span>TT$ 500.00</span>
              </div>
              <div className="payment-total">
                <span>Total</span>
                <span>TT$ 500.00</span>
              </div>
            </div>

            {message && <p className="success-message">{message}</p>}
            {error && <p className="error-message">{error}</p>}

            <button 
              onClick={handlePayment}
              disabled={processing}
              className="btn-pay"
            >
              {processing ? 'Processing...' : 'Pay TT$ 500'}
            </button>

            <p className="payment-secure">🔒 Secure payment via WiPay</p>
          </div>
        )}

        {/* Payment History */}
        <div className="subscription-card history-card">
          <h2>Payment History</h2>
          {subscription?.lastPaymentDate ? (
            <div className="payment-history">
              <div className="history-item">
                <div className="history-date">{formatDate(subscription.lastPaymentDate)}</div>
                <div className="history-amount">TT$ {subscription.lastPaymentAmount || 500}</div>
                <div className="history-status">✓ Paid</div>
              </div>
              {/* Additional history would be loaded from a payments subcollection */}
            </div>
          ) : (
            <p className="no-history">No payment history available</p>
          )}
        </div>

        {/* Help Section */}
        <div className="subscription-card help-card">
          <h2>Need Help?</h2>
          <p>If you have any questions about your subscription or billing, contact us:</p>
          <p>
            <strong>Email:</strong> <a href="mailto:support@clublinktt.com">support@clublinktt.com</a>
          </p>
          <p>
            <strong>Phone:</strong> (868) 123-4567
          </p>
        </div>
      </div>
    </AdminLayout>
  );
}

export default ManageSubscription;
